import type { Circuit, CircuitGrandstand, CircuitSign, CircuitVehicle } from '../types/track.js';
import { DECOR_VEHICLE_SPRITE_PX } from './circuitVehicleCatalog.js';
import { getCircuitVehicleSprite } from './circuitVehicleCatalog.js';

export interface CircuitPropDrawTarget {
  fillRect(x: number, y: number, w: number, h: number, color: string): void;
  strokeRect(x: number, y: number, w: number, h: number, color: string, lineWidth: number): void;
  fillCircle(x: number, y: number, radius: number, color: string): void;
  /** Ellipse pleine optionnelle — épaules / cheveux plus naturels sur canvas. */
  fillEllipse?(x: number, y: number, rx: number, ry: number, color: string): void;
  drawSprite?(spriteId: string, x: number, y: number, w: number, h: number): void;
  drawText(
    x: number,
    y: number,
    w: number,
    h: number,
    text: string,
    color: string,
    fontSize: number
  ): void;
}

const GRANDSTAND_SHADOW = 'rgba(15, 23, 42, 0.45)';
const GRANDSTAND_FRAME = '#64748b';
const GRANDSTAND_RAIL = '#e2e8f0';
const GRANDSTAND_RAIL_SHADOW = '#475569';
const GRANDSTAND_DIVIDER = 'rgba(15, 23, 42, 0.22)';

const TIER_SEAT_COLORS = ['#3d4f63', '#44586d', '#4b6177', '#526a81', '#5a738b'];
const TIER_STEP_COLORS = ['#2f3d4d', '#354556', '#3b4d5f', '#415568', '#475e71'];

/** Espacement horizontal entre silhouettes (fraction de cellSize). */
export const SPECTATOR_SPACING_RATIO = 0.42;

interface SpectatorPalette {
  skin: string;
  hair: string;
  shirt: string;
  shirtDark: string;
}

const SPECTATOR_PALETTES: SpectatorPalette[] = [
  { skin: '#fcd9b6', hair: '#3d2914', shirt: '#dc2626', shirtDark: '#991b1b' },
  { skin: '#f5c99a', hair: '#1f2937', shirt: '#2563eb', shirtDark: '#1d4ed8' },
  { skin: '#e8b88a', hair: '#78350f', shirt: '#16a34a', shirtDark: '#15803d' },
  { skin: '#f8d4c4', hair: '#4c1d95', shirt: '#7c3aed', shirtDark: '#6d28d9' },
  { skin: '#fcd9b6', hair: '#292524', shirt: '#ea580c', shirtDark: '#c2410c' },
  { skin: '#deb887', hair: '#0f172a', shirt: '#0891b2', shirtDark: '#0e7490' },
  { skin: '#fce7d6', hair: '#831843', shirt: '#db2777', shirtDark: '#be185d' },
  { skin: '#f5deb3', hair: '#374151', shirt: '#4b5563', shirtDark: '#374151' },
];

const SIGN_BOARD = '#fef9c3';
const SIGN_BORDER = '#a16207';
const SIGN_TEXT = '#422006';

interface StandGridBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

function hashSeed(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

function getStandGridBounds(cells: Array<{ x: number; y: number }>): StandGridBounds {
  let minX = cells[0].x;
  let maxX = cells[0].x;
  let minY = cells[0].y;
  let maxY = cells[0].y;
  for (const cell of cells) {
    minX = Math.min(minX, cell.x);
    maxX = Math.max(maxX, cell.x);
    minY = Math.min(minY, cell.y);
    maxY = Math.max(maxY, cell.y);
  }
  return { minX, minY, maxX, maxY };
}

function pseudoRandom(seed: number, index: number): number {
  const x = Math.sin(seed * 12.9898 + index * 78.233) * 43758.5453;
  return x - Math.floor(x);
}

function fillShoulders(
  target: CircuitPropDrawTarget,
  cx: number,
  cy: number,
  w: number,
  h: number,
  color: string
): void {
  if (target.fillEllipse) {
    target.fillEllipse(cx, cy, w / 2, h / 2, color);
    return;
  }
  target.fillRect(cx - w / 2, cy - h / 2, w, h, color);
}

function drawSpectator(
  target: CircuitPropDrawTarget,
  cx: number,
  seatY: number,
  scale: number,
  seed: number,
  spectatorIndex: number
): void {
  const paletteIdx = Math.floor(pseudoRandom(seed, spectatorIndex * 3) * SPECTATOR_PALETTES.length);
  const palette = SPECTATOR_PALETTES[paletteIdx % SPECTATOR_PALETTES.length];
  const cheering = pseudoRandom(seed, spectatorIndex * 7 + 11) > 0.78;

  const headR = Math.max(1.4, scale * 0.44);
  const neckH = Math.max(0.8, scale * 0.12);
  const torsoW = Math.max(2.2, scale * 0.58);
  const torsoH = Math.max(1.8, scale * 0.48);
  const shoulderW = Math.max(2.8, scale * 0.88);
  const shoulderH = Math.max(1.2, scale * 0.28);

  const torsoTop = seatY - torsoH;
  target.fillRect(cx - torsoW / 2, torsoTop, torsoW, torsoH, palette.shirtDark);
  target.fillRect(
    cx - torsoW * 0.42,
    torsoTop + scale * 0.04,
    torsoW * 0.84,
    torsoH * 0.9,
    palette.shirt
  );

  const shoulderY = torsoTop - shoulderH * 0.35;
  fillShoulders(target, cx, shoulderY, shoulderW, shoulderH, palette.shirt);

  target.fillRect(
    cx - scale * 0.1,
    torsoTop - neckH,
    Math.max(1, scale * 0.2),
    neckH,
    palette.skin
  );

  const headCy = torsoTop - neckH - headR * 0.85;

  if (cheering) {
    const armW = Math.max(1, scale * 0.15);
    const armH = Math.max(1.5, scale * 0.38);
    const armY = shoulderY - armH * 0.85;
    target.fillRect(cx - shoulderW * 0.72, armY, armW, armH, palette.skin);
    target.fillRect(cx + shoulderW * 0.72 - armW, armY, armW, armH, palette.skin);
    target.fillRect(
      cx - shoulderW * 0.55,
      shoulderY - scale * 0.1,
      armW * 1.1,
      scale * 0.2,
      palette.shirt
    );
    target.fillRect(
      cx + shoulderW * 0.55 - armW * 1.1,
      shoulderY - scale * 0.1,
      armW * 1.1,
      scale * 0.2,
      palette.shirt
    );
  }

  target.fillCircle(cx, headCy + headR * 0.05, headR * 1.12, palette.hair);
  target.fillCircle(cx, headCy, headR, palette.skin);
  target.fillRect(
    cx - headR * 0.82,
    headCy - headR * 1.15,
    headR * 1.64,
    headR * 0.55,
    palette.hair
  );

  if (scale >= 8) {
    const eyeR = Math.max(0.5, scale * 0.06);
    target.fillCircle(cx - headR * 0.35, headCy + headR * 0.08, eyeR, '#1e293b');
    target.fillCircle(cx + headR * 0.35, headCy + headR * 0.08, eyeR, '#1e293b');
  }
}

function drawGrandstandStructure(
  target: CircuitPropDrawTarget,
  stand: CircuitGrandstand,
  cellPx: (cell: { x: number; y: number }) => { x: number; y: number },
  cellSize: number
): void {
  const bounds = getStandGridBounds(stand.cells);
  const topLeft = cellPx({ x: bounds.minX, y: bounds.minY });
  const cols = bounds.maxX - bounds.minX + 1;
  const rows = bounds.maxY - bounds.minY + 1;
  const blockW = cols * cellSize;
  const blockH = rows * cellSize;

  const pad = Math.max(1.5, cellSize * 0.05);
  const x = topLeft.x + pad;
  const y = topLeft.y + pad;
  const w = blockW - pad * 2;
  const h = blockH - pad * 2;

  const seed = hashSeed(stand.id);
  const shadowOffset = Math.max(1, cellSize * 0.04);
  target.fillRect(x + shadowOffset, y + shadowOffset, w, h, GRANDSTAND_SHADOW);

  const tierCount = Math.max(2, Math.min(5, Math.round(rows * 1.6 + (cols > 1 ? 0.5 : 0))));
  const tierH = h / tierCount;
  const railH = Math.max(2, cellSize * 0.1);
  const stepH = Math.max(1.5, cellSize * 0.07);

  for (let tier = 0; tier < tierCount; tier++) {
    const tierY = y + tier * tierH;
    const seatColor = TIER_SEAT_COLORS[tier % TIER_SEAT_COLORS.length];
    const stepColor = TIER_STEP_COLORS[tier % TIER_STEP_COLORS.length];
    const inset = tier * Math.max(0.5, cellSize * 0.02);
    const seatX = x + inset;
    const seatW = w - inset * 2;
    const seatAreaH = tierH - stepH;

    target.fillRect(seatX, tierY, seatW, seatAreaH, seatColor);
    target.fillRect(seatX, tierY + seatAreaH, seatW, stepH, stepColor);

    const spectators = Math.max(1, Math.floor(seatW / (cellSize * SPECTATOR_SPACING_RATIO)));
    const scale = Math.min(cellSize * 0.32, seatAreaH * 0.62);
    const rowY = tierY + seatAreaH * 0.68;

    for (let s = 0; s < spectators; s++) {
      const jitter = (pseudoRandom(seed, tier * 41 + s) - 0.5) * cellSize * 0.06;
      const cx = seatX + ((s + 0.5) * seatW) / spectators + jitter;
      drawSpectator(target, cx, rowY, scale, seed + tier * 100, tier * 31 + s);
    }
  }

  for (let gx = bounds.minX + 1; gx <= bounds.maxX; gx++) {
    const dividerX = cellPx({ x: gx, y: bounds.minY }).x;
    target.fillRect(dividerX, y, Math.max(1, cellSize * 0.03), h, GRANDSTAND_DIVIDER);
  }

  for (let gy = bounds.minY + 1; gy <= bounds.maxY; gy++) {
    const dividerY = cellPx({ x: bounds.minX, y: gy }).y;
    target.fillRect(x, dividerY, w, Math.max(1, cellSize * 0.03), GRANDSTAND_DIVIDER);
  }

  target.strokeRect(x, y, w, h, GRANDSTAND_FRAME, Math.max(1, cellSize * 0.05));
  target.fillRect(x, y + h - railH, w, railH, GRANDSTAND_RAIL_SHADOW);
  target.fillRect(x, y + h - railH * 0.65, w, Math.max(1, railH * 0.35), GRANDSTAND_RAIL);
  target.strokeRect(x, y, w, h, GRANDSTAND_FRAME, Math.max(1.2, cellSize * 0.06));

  const postW = Math.max(1.5, cellSize * 0.06);
  const postCount = Math.max(2, cols + 1);
  for (let p = 0; p < postCount; p++) {
    const px = x + (p * w) / (postCount - 1) - postW / 2;
    target.fillRect(px, y, postW, railH * 1.8, GRANDSTAND_RAIL);
  }
}

function wrapSignText(text: string, maxCharsPerLine: number, maxLines: number): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    if (next.length <= maxCharsPerLine) {
      current = next;
      continue;
    }
    if (current) lines.push(current);
    current = word.length > maxCharsPerLine ? word.slice(0, maxCharsPerLine) : word;
    if (lines.length >= maxLines) break;
  }
  if (lines.length < maxLines && current) lines.push(current);
  return lines.slice(0, maxLines);
}

function drawSign(
  target: CircuitPropDrawTarget,
  sign: CircuitSign,
  px: number,
  py: number,
  cellSize: number
): void {
  const w = sign.width * cellSize;
  const h = sign.height * cellSize;
  const pad = Math.max(2, cellSize * 0.1);
  const boardX = px + pad;
  const boardY = py + pad;
  const boardW = w - pad * 2;
  const boardH = h - pad * 2;
  const postH = Math.max(2, cellSize * 0.14);
  const postW = Math.max(2, cellSize * 0.07);

  if (sign.height > 1 || sign.width > 1) {
    target.fillRect(boardX, boardY + boardH - postH, postW, postH, SIGN_BORDER);
    target.fillRect(boardX + boardW - postW, boardY + boardH - postH, postW, postH, SIGN_BORDER);
  }

  target.fillRect(boardX, boardY, boardW, boardH, SIGN_BOARD);
  target.strokeRect(boardX, boardY, boardW, boardH, SIGN_BORDER, Math.max(1.5, cellSize * 0.08));

  const maxLines = Math.max(1, sign.height * 2);
  const maxChars = Math.max(4, sign.width * 4);
  const lines = wrapSignText(sign.text, maxChars, maxLines);
  const fontSize = Math.max(
    8,
    Math.min(cellSize * (0.28 + sign.width * 0.04), (boardH - pad) / (lines.length + 0.5))
  );
  const lineHeight = fontSize * 1.15;
  const textBlockH = lines.length * lineHeight;
  let ty = boardY + (boardH - textBlockH) / 2 + fontSize * 0.85;
  for (const line of lines) {
    target.drawText(boardX, ty - fontSize, boardW, lineHeight, line, SIGN_TEXT, fontSize);
    ty += lineHeight;
  }
}

export function drawCircuitGrandstands(
  target: CircuitPropDrawTarget,
  grandstands: CircuitGrandstand[] | undefined,
  cellPx: (cell: { x: number; y: number }) => { x: number; y: number },
  cellSize: number
): void {
  for (const stand of grandstands ?? []) {
    if (stand.cells.length === 0) continue;
    drawGrandstandStructure(target, stand, cellPx, cellSize);
  }
}

const VEHICLE_PLACEHOLDER = '#64748b';
const VEHICLE_PLACEHOLDER_BG = 'rgba(30, 41, 59, 0.55)';

function drawVehiclePlaceholder(
  target: CircuitPropDrawTarget,
  vehicle: CircuitVehicle,
  px: number,
  py: number,
  cellSize: number
): void {
  const pad = Math.max(1, cellSize * 0.06);
  const size = cellSize - pad * 2;
  target.fillRect(px + pad, py + pad, size, size, VEHICLE_PLACEHOLDER_BG);
  target.strokeRect(
    px + pad,
    py + pad,
    size,
    size,
    VEHICLE_PLACEHOLDER,
    Math.max(1, cellSize * 0.04)
  );
  const label = getCircuitVehicleSprite(vehicle.spriteId)?.label ?? vehicle.spriteId;
  const fontSize = Math.max(7, Math.min(cellSize * 0.22, size * 0.35));
  target.drawText(px + pad, py + pad, size, size, label.slice(0, 6), '#e2e8f0', fontSize);
}

function drawVehicle(
  target: CircuitPropDrawTarget,
  vehicle: CircuitVehicle,
  px: number,
  py: number,
  cellSize: number
): void {
  const pad = Math.max(1, cellSize * 0.06);
  const size = cellSize - pad * 2;
  const drawX = px + pad;
  const drawY = py + pad;
  if (target.drawSprite) {
    target.drawSprite(vehicle.spriteId, drawX, drawY, size, size);
    return;
  }
  drawVehiclePlaceholder(target, vehicle, px, py, cellSize);
}

export function drawCircuitVehicles(
  target: CircuitPropDrawTarget,
  vehicles: CircuitVehicle[] | undefined,
  cellPx: (cell: { x: number; y: number }) => { x: number; y: number },
  cellSize: number
): void {
  for (const vehicle of vehicles ?? []) {
    const { x: px, y: py } = cellPx(vehicle);
    drawVehicle(target, vehicle, px, py, cellSize);
  }
}

/** Taille d’affichage d’un sprite véhicule pour une case donnée. */
export function decorVehicleDisplaySize(cellSize: number): number {
  const pad = Math.max(1, cellSize * 0.06);
  return cellSize - pad * 2;
}

export function decorVehicleSpriteScale(cellSize: number): number {
  return decorVehicleDisplaySize(cellSize) / DECOR_VEHICLE_SPRITE_PX;
}

export function drawCircuitSigns(
  target: CircuitPropDrawTarget,
  signs: CircuitSign[] | undefined,
  cellPx: (cell: { x: number; y: number }) => { x: number; y: number },
  cellSize: number
): void {
  for (const sign of signs ?? []) {
    const { x: px, y: py } = cellPx(sign);
    drawSign(target, sign, px, py, cellSize);
  }
}

export function drawCircuitProps(
  target: CircuitPropDrawTarget,
  circuit: Circuit,
  cellPx: (cell: { x: number; y: number }) => { x: number; y: number },
  cellSize: number,
  options?: { drawVehicles?: boolean }
): void {
  drawCircuitGrandstands(target, circuit.grandstands, cellPx, cellSize);
  if (options?.drawVehicles !== false) {
    drawCircuitVehicles(target, circuit.vehicles, cellPx, cellSize);
  }
  drawCircuitSigns(target, circuit.signs, cellPx, cellSize);
}

export function createCanvasPropDrawTarget(ctx: CanvasRenderingContext2D): CircuitPropDrawTarget {
  return {
    fillRect: (x, y, w, h, color) => {
      ctx.fillStyle = color;
      ctx.fillRect(x, y, w, h);
    },
    strokeRect: (x, y, w, h, color, lineWidth) => {
      ctx.strokeStyle = color;
      ctx.lineWidth = lineWidth;
      ctx.strokeRect(x, y, w, h);
    },
    fillCircle: (x, y, radius, color) => {
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
    },
    fillEllipse: (x, y, rx, ry, color) => {
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2);
      ctx.fill();
    },
    drawText: (x, _y, w, _h, text, color, fontSize) => {
      ctx.fillStyle = color;
      ctx.font = `600 ${fontSize}px Inter, system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, x + w / 2, _y + _h / 2);
    },
  };
}

/** Utilisé par les tests — nombre de silhouettes par rangée de gradin. */
export function countGrandstandSpectatorsForWidth(cellSize: number, seatWidth: number): number {
  return Math.max(1, Math.floor(seatWidth / (cellSize * SPECTATOR_SPACING_RATIO)));
}
